import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import PropTypes from 'prop-types';
import DataGridProvider, { DataGridContext } from './context';
import DataGridStateManager from './DataGridStateManager';
import { LicenseInfo } from '@mui/x-license';
import {
  derivePinnedColumnsFromOverrides,
  normalizePinnedColumns,
} from './utils/pinnedColumnsUtils';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || '');

// Define TypeScript types for props
interface DataGridMAIQProps {
  type: any;
  onRowClick?: (row: any) => void;
  onCellClick?: (cell: any, clickedElement?: string, extraData?: any) => void;
  onMultiSelect?: (selectionModel: any[], selectedRows?: any[]) => void;
  /** Bump to refetch grid data in place (React `key` is not passed as a prop). */
  refreshKey?: string | number;
  manualUpdateData?: (rowId: string, updates: Record<string, any>) => void;
  toolbarRightSlot?: React.ReactNode;
  /** Called when server meta totalDocuments updates (e.g. HIL summary card sync). */
  onTotalDocumentsChange?: (totalDocuments: number) => void;
  /** Called when server meta is returned from a grid fetch. */
  onGridMetaChange?: (meta: Record<string, unknown>) => void;
  /** Client-side row filter by `_id` after fetch (e.g. resolve modal metadata template_ids). */
  allowedRowIds?: string[];
}

// Define the ref interface
export interface DataGridMAIQRef {
  manualUpdateData: (rowId: string, updates: Record<string, any>) => void;
}

// Inner component that has access to context
const DataGridMAIQInner = forwardRef<DataGridMAIQRef, DataGridMAIQProps>(({
  type,
  onRowClick = () => {},
  onCellClick = () => {},
  onMultiSelect = () => {},
  refreshKey,
  manualUpdateData,
  toolbarRightSlot,
  onTotalDocumentsChange,
}, ref) => {
  const dataGridContext = useContext(DataGridContext);

  // Expose the manualUpdateData function from context through ref
  useImperativeHandle(ref, () => ({
    manualUpdateData: (rowId: string, updates: Record<string, any>) => {
      if (dataGridContext?.manualUpdateData) {
        dataGridContext.manualUpdateData(rowId, updates);
      }
    }
  }), [dataGridContext?.manualUpdateData]);

  const {
    apiDetails,
    pageMode = 'scroll',
    pageSize = 0,
    filterable = false,
    renderMode = 'server',
    editable = false,
    gridHeight,
    autoHeightBuffer = 0,
    exportable = false,
    exportFileName = 'export',
    exportColumns = 'all',
    columnResizable = true,
    isRowSelectable = true,
    isOpportunity = true,
    multiSelectable = false,
    renderCellMapping = [
      {
        field: 'name',
        renderCell: 'TEXT',
      },
    ],
    columnSortModel = [],
    visibleColumns = [],
    filterableFields = [],
  } = type;
  let apiEndpoint: string = `${apiDetails.endpoint}`;
  for (const param in apiDetails.pathParams) {
    apiEndpoint += `/${apiDetails.pathParams[param]}`;
  }

  const pinnedColumns = normalizePinnedColumns(
    type.pinnedColumns ?? derivePinnedColumnsFromOverrides(type.columnDefOverrides),
  );

  return (
    <DataGridStateManager
      apiEndpoint={apiEndpoint}
      params={apiDetails.queryParams}
      pageMode={pageMode}
      pageSize={pageSize}
      rendermode={renderMode}
      filterable={filterable}
      editable={editable}
      gridHeight={gridHeight?.toString()}
      autoHeightBuffer={autoHeightBuffer}
      onRowClick={onRowClick}
      onMultiSelect={onMultiSelect}
      exportable={exportable}
      exportFileName={exportFileName}
      exportColumns={exportColumns}
      checkboxSelection={multiSelectable}
      columnResizable={columnResizable}
      isRowSelectable={isRowSelectable}
      isOpportunity={isOpportunity}
      renderCellMapping={renderCellMapping}
      multiSelectable={multiSelectable}
      toolbarRightSlot={toolbarRightSlot}
      refreshKey={refreshKey}
      pinnedColumns={pinnedColumns}
    />
  );
});

// Main component
const DataGridMAIQ = forwardRef<DataGridMAIQRef, DataGridMAIQProps>(({
  type,
  onRowClick = () => {},
  onCellClick = () => {},
  onMultiSelect = () => {},
  refreshKey,
  manualUpdateData,
  toolbarRightSlot,
  onTotalDocumentsChange,
  onGridMetaChange,
  allowedRowIds,
}, ref) => {
  const {
    apiDetails,
    pageMode = 'scroll',
    pageSize = 0,
    filterable = false,
    renderMode = 'server',
    editable = false,
    gridHeight,
    autoHeightBuffer = 0,
    exportable = false,
    exportFileName = 'export',
    exportColumns = 'all',
    columnResizable = true,
    isRowSelectable = true,
    isOpportunity = true,
    multiSelectable = false,
    renderCellMapping = [
      {
        field: 'name',
        renderCell: 'TEXT',
      },
    ],
    columnSortModel = [],
    visibleColumns = [],
    filterableFields = [],
  } = type;

  return (
    <DataGridProvider
      renderCellMapping={renderCellMapping}
      onCellClick={onCellClick}
      sortModel={columnSortModel}
      visibleColumns={visibleColumns}
      filterableFields={filterableFields}
      externalManualUpdate={manualUpdateData}
      initialFilterModel={type.initialFilterModel}
      columnDefOverrides={type.columnDefOverrides}
      onTotalDocumentsChange={onTotalDocumentsChange}
      onGridMetaChange={onGridMetaChange}
      allowedRowIds={allowedRowIds}
      apiHeadersOnly={type.apiHeadersOnly ?? false}
    >
      <DataGridMAIQInner
        type={type}
        onRowClick={onRowClick}
        onCellClick={onCellClick}
        onMultiSelect={onMultiSelect}
        refreshKey={refreshKey}
        toolbarRightSlot={toolbarRightSlot}
        ref={ref}
      />
    </DataGridProvider>
  );
});

// PropTypes for runtime validation
DataGridMAIQ.propTypes = {
  type: PropTypes.any.isRequired,
};

DataGridMAIQ.displayName = 'DataGridMAIQ';

export default DataGridMAIQ;
